import React, { useState, useEffect, useCallback } from "react";
import CustomHeader from "../../components/header/index.jsx";
import url from "../../config.js";
import "./superadmin.scss";

const domain = url.domain;

const authHeaders = () => ({
  "Content-Type": "application/json",
  "x-access-token": localStorage.getItem("token") || "",
});

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const daysLeft = (end) => {
  if (!end) return null;
  const ms = new Date(end).getTime() - Date.now();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
};

function SuperAdmin() {
  const [plans, setPlans] = useState({});          // { key: {label, maxUsers, durationDays} }
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Add-tenant form
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [plan, setPlan] = useState("free");
  const [adminEmail, setAdminEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  // Per-row plan management
  const [rowBusy, setRowBusy] = useState({});
  const [planChoice, setPlanChoice] = useState({});

  const loadTenants = useCallback(() => {
    setLoading(true);
    fetch(`${domain}/api/v1/tenants/all`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => setTenants(Array.isArray(data) ? data : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetch(`${domain}/api/v1/subscription-plans`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        setPlans(data || {});
        const first = Object.keys(data || {})[0];
        if (first) setPlan(first);
      })
      .catch(() => {});
    loadTenants();
  }, [loadTenants]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");
    if (!name.trim()) { setFormError("Tenant name is required."); return; }
    const emailRe = /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i;
    if (!emailRe.test(adminEmail.trim())) { setFormError("Enter a valid tenant-admin email."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${domain}/api/v1/tenants`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ name: name.trim(), description: description.trim(), plan, adminEmail: adminEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Could not create tenant.");
      if (data.inviteError) {
        setFormSuccess(`Tenant "${name.trim()}" created, but the admin invite failed: ${data.inviteError}`);
      } else {
        setFormSuccess(`Tenant "${name.trim()}" created and an invite was sent to ${adminEmail.trim()}.`);
      }
      setName(""); setDescription(""); setAdminEmail("");
      loadTenants();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const patchTenant = async (id, body) => {
    setRowBusy((p) => ({ ...p, [id]: true }));
    try {
      const res = await fetch(`${domain}/api/v1/tenants/${id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Update failed.");
      loadTenants();
    } catch (e) {
      alert(e.message);
    } finally {
      setRowBusy((p) => { const n = { ...p }; delete n[id]; return n; });
    }
  };

  const statusChip = (t) => {
    const dl = daysLeft(t.subscriptionEnd);
    let mod = "ok", text = t.status || "active";
    if (t.status === "suspended" || t.status === "expired" || (dl != null && dl < 0)) { mod = "err"; text = "expired"; }
    else if (dl != null && dl <= 7) { mod = "warn"; text = `${dl}d left`; }
    else if (dl != null) { text = `${dl}d left`; }
    return <span className={`sa-chip sa-chip--${mod}`}>{text}</span>;
  };

  return (
    <div className="root">
      <CustomHeader />
      <main className="content">
        <div className="sa-page">
          <h1 className="sa-title">Super Admin</h1>
          <p className="sa-subtitle">Provision tenants, choose a subscription plan, and invite each tenant's administrator.</p>

          {/* Add tenant */}
          <div className="sa-card sa-card--form">
            <h2 className="sa-card-title">Add a tenant</h2>
            <form onSubmit={handleCreate}>
              <div className="sa-grid">
                <div>
                  <label className="sa-label">Tenant name *</label>
                  <input className="sa-input" value={name} onChange={e => setName(e.target.value)} placeholder="Acme Corp" />
                </div>
                <div>
                  <label className="sa-label">Tenant admin email *</label>
                  <input className="sa-input" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} placeholder="admin@acme.com" />
                </div>
                <div>
                  <label className="sa-label">Subscription plan *</label>
                  <select className="sa-input" value={plan} onChange={e => setPlan(e.target.value)}>
                    {Object.keys(plans).map(k => (
                      <option key={k} value={k}>
                        {plans[k].label} — {plans[k].maxUsers} users · {plans[k].durationDays} days
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="sa-label">Description</label>
                  <input className="sa-input" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
                </div>
              </div>

              {plans[plan] && (
                <div className="sa-hint">
                  <strong>{plans[plan].label}</strong>: up to <strong>{plans[plan].maxUsers}</strong> users, valid <strong>{plans[plan].durationDays}</strong> days from creation.
                </div>
              )}

              {formError && <div className="sa-error">{formError}</div>}
              {formSuccess && <div className="sa-success">{formSuccess}</div>}

              <button type="submit" className="sa-btn" disabled={submitting}>
                {submitting ? "Creating…" : "Create tenant & invite admin"}
              </button>
            </form>
          </div>

          {/* Tenants table */}
          <div className="sa-card sa-card--table">
            <div className="sa-table-head">
              <h2 className="sa-card-title" style={{ margin: 0 }}>Tenants</h2>
              <button className="sa-refresh" onClick={loadTenants}>Refresh</button>
            </div>
            {error && <div className="sa-error" style={{ padding: 16, margin: 0 }}>{error}</div>}
            <div className="sa-table-wrap">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Plan</th>
                    <th>Seats</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th>Status</th>
                    <th>Manage</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7}>Loading…</td></tr>
                  ) : tenants.length === 0 ? (
                    <tr><td colSpan={7}>No tenants yet. Add one above.</td></tr>
                  ) : tenants.map(t => {
                    const used = (t.usersCount || 0) + (t.pendingInvites || 0);
                    return (
                      <tr key={t._id}>
                        <td>
                          <div className="sa-tenant-name">{t.name || "—"}</div>
                          {t.description && <div className="sa-tenant-desc">{t.description}</div>}
                        </td>
                        <td>{plans[t.plan]?.label || t.plan || "—"}</td>
                        <td>
                          {used}{t.maxUsers != null ? ` / ${t.maxUsers}` : ""}
                          {t.pendingInvites ? <span className="sa-seats-pending"> ({t.pendingInvites} pending)</span> : null}
                        </td>
                        <td>{fmtDate(t.createdAt || t.subscriptionStart)}</td>
                        <td>{fmtDate(t.subscriptionEnd)}</td>
                        <td>{statusChip(t)}</td>
                        <td>
                          <div className="sa-actions">
                            <select
                              className="sa-plan-select"
                              value={planChoice[t._id] ?? t.plan ?? ""}
                              disabled={rowBusy[t._id]}
                              onChange={(e) => { const value = e.target.value; setPlanChoice((p) => ({ ...p, [t._id]: value })); }}>
                              {Object.keys(plans).map((k) => (
                                <option key={k} value={k}>{plans[k].label}</option>
                              ))}
                            </select>
                            <button className="sa-mini" disabled={rowBusy[t._id]}
                              onClick={() => patchTenant(t._id, { plan: planChoice[t._id] ?? t.plan })}>
                              Change plan
                            </button>
                            <button className="sa-mini" disabled={rowBusy[t._id]}
                              onClick={() => patchTenant(t._id, { extendDays: 30 })}>
                              +30d
                            </button>
                            {t.status === "suspended" ? (
                              <button className="sa-mini" disabled={rowBusy[t._id]}
                                onClick={() => patchTenant(t._id, { status: "active" })}>
                                Reactivate
                              </button>
                            ) : (
                              <button className="sa-mini sa-mini--warn" disabled={rowBusy[t._id]}
                                onClick={() => patchTenant(t._id, { status: "suspended" })}>
                                Suspend
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default SuperAdmin;
